import { DataListOption } from "src/api/sure-api/dataList/useGetAllDataList";
import { PropertyValue } from "./Builder/PropertyBuilder";

const emptyProperty = "_empty_" as const;
const multivalueSplittingCharacter = "|" as const;

type SerializedValueForBackend = {
    value: string | "_empty_";
    dataListOptionCodes: string[];
};

type ReadonlyValue = string | number | (string | number)[];
type ReadonlyType = {
    labels: ReadonlyValue;
};
export const deserializeReadOnly = (value: PropertyValue): ReadonlyType => {
    if (value.fieldType === "number" || value.fieldType === "text" || value.fieldType === "long_text") {
        const values = deserializeValueProperty(value);
        return { labels: values.value };
    }
    if (value.fieldType === "radio") {
        const values = deserializeOptionsFromProperty(value);
        return { labels: values.value?.label ?? "" };
    }
    if (value.fieldType === "select") {
        const values = deserializeOptionsFromProperty(value);
        return { labels: values.value?.label ?? "" };
    }
    if (value.fieldType === "checkbox") {
        const values = deserializeManyProperty(value);
        const manyValues = values.value.map((i) => i.label);
        return { labels: manyValues };
    }
    throw new Error("Shouldn't happen. Contact your admin (101)");
};

export const serializeValueProperty = (value: string): SerializedValueForBackend => {
    return {
        value: value ?? "_empty_",
        dataListOptionCodes: [],
    };
};

type OneValue = {
    value: string;
    defaultValue: string | undefined;
    genericValue: string | undefined;
};
export const deserializeValueProperty = (value: PropertyValue): OneValue => {
    if (value.type !== "value") {
        // Maybe throw
        return {
            value: "",
            defaultValue: undefined,
            genericValue: undefined,
        };
    }
    const usable: OneValue = {
        value: value.value ?? emptyProperty,
        defaultValue: value.property.defaultValue,
        genericValue: value.property.genericValue,
    };
    return usable;
};

export const serializeOptionsFromProperty = (value: DataListOption | undefined): SerializedValueForBackend => {
    return {
        value: value?.label ?? "_empty",
        dataListOptionCodes: value ? [value.code] : [],
    };
};

type OneValueFromOption = {
    value: DataListOption | undefined;
    defaultValue: DataListOption | undefined;
    genericValue: DataListOption | undefined;
};
export const deserializeOptionsFromProperty = (value: PropertyValue): OneValueFromOption => {
    const options = value.property.dataList?.options ?? [];
    if (options.length === 0) {
        // Maybe throw
        const emptyValue: OneValueFromOption = {
            value: undefined,
            defaultValue: undefined,
            genericValue: undefined,
        };
        return emptyValue;
    }

    const defaultToFind = value.property.defaultValue;
    const genericToFind = value.property.genericValue;

    const foundDefault = options.find((option: DataListOption) => option.label === defaultToFind);
    const foundGeneric = options.find((option: DataListOption) => option.label === genericToFind);

    // This commented line is supposed to be the new way of displaying `oneValueFromOptionList`
    // But the data from back-end being inconsistent, we need to be using the previous way of handling
    // this use-case;

    // Ilyes: This line is now functional since the 2025-06-24 update from the back-end
    const foundValue = options.find((option) => value.dataListOptionCodes.includes(option.code));

    // Ilyes: Old way of handling the `foundValue` which is now deprecated
    const deprecatedFoundValue = options.find((option) => option.value === value.value);

    const usable: OneValueFromOption = {
        value: foundValue ?? deprecatedFoundValue,
        defaultValue: foundDefault,
        genericValue: foundGeneric,
    };

    return usable;
};

export const serializeManyProperty = (values: DataListOption[]): SerializedValueForBackend => {
    const deserializedValues = values.join(multivalueSplittingCharacter);
    const dataListOptionCodes = values.map((v) => v.code);
    return {
        value: deserializedValues,
        dataListOptionCodes,
    };
};

type ManyValuesFromOption = {
    value: DataListOption[];
    defaultValue: DataListOption[];
    genericValue: DataListOption[];
};
export const deserializeManyProperty = (value: PropertyValue): ManyValuesFromOption => {
    const options = value.property.dataList?.options ?? [];
    if (options.length === 0) {
        return {
            value: [],
            defaultValue: [],
            genericValue: [],
        };
    }
    const usable: ManyValuesFromOption = {
        value: [],
        defaultValue: [],
        genericValue: [],
    };
    return usable;
};
